import React, { useState, useEffect, Fragment, useRef } from 'react';
import IncomeSection from './IncomeSection';
import ExpenseSection from './ExpenseSection';
import MonthlyIncomeSection from './MonthlyIncomeSection';
import LongFormMeansTestSection from './LongFormMeansTestSection';
import { generateScheduleIJ } from './IJGenerators';
import MailingMatrixServiceSelector from '../components/MailingMatrixServiceSelector';

import { useParams } from 'react-router-dom';
  // Calculate monthly income from rate, schedule, and units
  const calcMonthlyIncome = (rate, schedule, units) => {
    const r = Number(rate);
    const u = units ? Number(units) : null;
    switch (schedule) {
      case 'hourly':
        // assume units = hours per week
        const hrs = u !== null ? u : 40;
        return (r * hrs * 52) / 12;
      case 'weekly':
        return (r * 52) / 12;
      case 'biweekly':
        return (r * 26) / 12;
      case 'semimonthly':
        return r * 2;
      case 'monthly':
        return r;
      case 'yearly':
        return r / 12;
      default:
        return 0;
    }
  };

  // Compute each debtor’s net employment + fixed + business income
const computeNetIncome = (debtors) => {
  if (!Array.isArray(debtors)) return 0;
  return debtors.reduce((total, d) => {
    // 1) Employment net of deductions
    const empNet = (d.employments || []).reduce((sum, emp) => {
      const monthly = calcMonthlyIncome(emp.pay_rate, emp.pay_schedule, emp.pay_units);
      const ded = (emp.deductions || []).reduce((ds, dt) => ds + Number(dt.amount||0), 0);
      return sum + (monthly - ded);
    }, 0);

    // 2) Fixed income fields
    const fixedKeys = [
      'interest_dividend_income',
      'family_support_payments',
      'unemployment_income',
      'social_security_income',
      'pension_income'
    ];
    const fixedSum = fixedKeys.reduce((fs, key) => fs + Number(d[key]||0), 0);

    // Government assistance
    const govSum = (d.gov_assistances || []).reduce((gs, ga) => gs + Number(ga.amount || 0), 0);
    // Other income items
    const otherSum = (d.other_income_items || []).reduce((os, oi) => os + Number(oi.amount || 0), 0);
    // Expense contributions
    const contribSum = (d.expense_contributions || []).reduce((cs, ec) => cs + Number(ec.amount || 0), 0);

    // 3) Business net (gross minus business expenses)
    const bizNet = (d.business_incomes || []).reduce((bs, biz) => {
      const exSum = (biz.expenses||[]).reduce((es, e) => es + Number(e.amount||0), 0);
      return bs + (Number(biz.gross_revenue||0) - exSum);
    }, 0);

    return total + empNet + fixedSum + govSum + otherSum + contribSum + bizNet;
  }, 0);
};
  const formatLocalDate = isoString => {
    if (!isoString) return null;
    // Expecting YYYY-MM-DD or ISO; split by '-' for date part
    const [year, month, day] = isoString.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString();
  };
  // Helper to display ISO datetime strings with date and time
  const formatLocalDateTime = isoString => {
    if (!isoString) return null;
    // Parse as local time, ignoring any 'Z' or timezone offset
    const [datePart, timePartWithOffset] = isoString.split('T');
    const [hour, minute] = timePartWithOffset.split(':');
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day, Number(hour), Number(minute)).toLocaleString();
  };
export default function ScheduleIJ() {

    const handleGenerate = async () => {
      const IJ = await generateScheduleIJ(
        caseData,
        expenses,
        expenseSheet,
        dependents,
        installments,
        selectedSchedules,
        data,
        meansTestIncomes,
        maritalAdjustments,
        longFormData,
      );

      if (IJ) {
      const IJLink = document.createElement('a');
      IJLink.href = URL.createObjectURL(IJ);
      IJLink.download = 'IJ';
      IJLink.click();
      }


    };
    const [meansTestIncomes, setMeansTestIncomes] = useState([]);
const [maritalAdjustments, setMaritalAdjustments] = useState([]);
  const [longFormData, setLongFormData] = useState({});
  const [longFormReady, setLongFormReady] = useState(false);
  const [showEditIncome, setShowEditIncome] = useState(false);
  const [editingIncomeData, setEditingIncomeData] = useState(null);
  const saveIncomeRef = useRef(null);
  const [data, setData] = useState({
    debtsD: [], // array of secured creditors
    debtsE: [], // array of priority creditors
    debtsF: [], // array of nonpriority unsecured creditors
    debtsG: [],
    notifyEF: [], // array of others to notify
    allDebts: [],
    caseInfo: {}, // case metadata: number, name, etc.
    amended: false,
    amendedReason: '',
    amendedCreditors: [],
    mailingMatrixService: null,
  });
  const [showAmendedModal, setShowAmendedModal] = useState(false);
  const [amendedReason, setAmendedReason] = useState('');
  const [amendStep, setAmendStep] = useState(1);
  const [amendedCreditors, setAmendedCreditors] = useState([]);
  const [mailingMatrixService, setMailingMatrixService] = useState(null);
  const [creditors, setCreditors] = useState([]);
  const [selectedSchedules, setSelectedSchedules] = useState(['all']);
  const { caseId } = useParams();
  const API_URL = process.env.REACT_APP_API_URL;
  const TOKEN = localStorage.getItem("accessToken");
  const [loading, setLoading] = useState(true);
  const [incomeItems, setIncomeItems] = useState([]);
  // compute total net income for both debtors
  
  const [caseData, setCaseData] = useState(null);
  const totalIncome = computeNetIncome(caseData?.debtors || []);
  const [expenses, setExpenses] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [dependents, setDependents] = useState([]);
  const [expenseSheet, setExpenseSheet] = useState(null);

useEffect(() => {
  async function fetchAll() {
    try {
      const caseRes = await fetch(`${API_URL}/case/${caseId}/overview/`, {
        headers: { Authorization: `Bearer ${TOKEN}` },
      });
      if (!caseRes.ok) throw new Error("Couldn't load case overview");
      setCaseData(await caseRes.json());
    } catch (e) {
      console.error(e);
    }
  }
  async function fetchData() {
    try {
      const token = localStorage.getItem("accessToken");
      // Fetch case details and creditors in parallel
      const [caseRes, credRes] = await Promise.all([
        fetch(`${API_URL}/case/${caseId}/creditors/case-details/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/case/${caseId}/creditors/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (!caseRes.ok || !credRes.ok) {
        console.error("Failed to fetch case or creditors");
        return;
      }
      const caseJson = await caseRes.json();
      const credJson = await credRes.json();
      // Populate data object
      setData({
        caseInfo: caseJson,
        debtsD: credJson.filter(d => d.debt_type === "secured"),
        debtsE: credJson.filter(d => d.debt_type === "priority"),
        debtsF: credJson.filter(d => d.debt_type === "unsecured"),
        debtsG: credJson.filter(d => d.debt_type === "executory"),
        notifyEF: credJson.filter(d => d.additional_notified),
        allDebts : credJson,
      });
      // Build a simple address list for UI display
      const creditorAddresses = credJson.map(c => ({
        name: c.account_name || c.name,
        street1: c.street1 || '',
        street2: c.street2 || '',
        city: c.city || '',
        state: c.state || '',
        zip_code: c.zip_code || '',
        codebtor: c.codebtor || '',
        codebtor_street1:c.codebtor_street1 || '',
        codebtor_street2:c.codebtor_street2 || '',
        codebtor_state:c.codebtor_state || '',
        codebtor_zip_code:c.codebtor_zip_code|| '',
        codebtor_city:c.codebtor_city||'',
      }));
      setCreditors(creditorAddresses);
    } catch (err) {
      console.error("Error fetching schedule data:", err);
    } finally {
      setLoading(false);
    }
  }
  (async () => {
    await fetchAll();
    await fetchData();

    // Fetch expense-related data
    const [expRes, payRes, depRes, sheetRes, incomesRes,adjRes] = await Promise.all([
      fetch(`${API_URL}/cases/${caseId}/expenses/`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
      fetch(`${API_URL}/cases/${caseId}/installment-payments/`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
      fetch(`${API_URL}/cases/${caseId}/dependents/`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
      fetch(`${API_URL}/cases/${caseId}/expense-sheet/`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
      fetch(`${API_URL}/case/${caseId}/means-test/incomes/`,{ headers: { Authorization: `Bearer ${TOKEN}` } }),
      fetch(`${API_URL}/case/${caseId}/means-test/marital-adjustments/`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
    ]);
    if (expRes.ok) setExpenses(await expRes.json());
    if (payRes.ok) setInstallments(await payRes.json());
    if (depRes.ok) setDependents(await depRes.json());
    if (sheetRes.ok) setExpenseSheet(await sheetRes.json());
    if (incomesRes.ok) setMeansTestIncomes(await incomesRes.json());
    if (adjRes.ok) setMaritalAdjustments(await adjRes.json());
    // Fetch income items after fetching expense sheet
    const incomeRes = await fetch(`${API_URL}/cases/${caseId}/income-items/`, {
      headers: { Authorization: `Bearer ${TOKEN}` }
    });
    if (incomeRes.ok) {
      const incomeJson = await incomeRes.json();
      setIncomeItems(incomeJson);
    } else {
      setIncomeItems([]); // fallback to empty array if fetch fails
    }
    setLongFormReady(true);
  })();
}, [caseId, API_URL, TOKEN]);

  // Top-level return JSX with MonthlyIncomeSection above IncomeSection
  return (
    <div className="w-full">
      {showAmendedModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow max-w-md w-full">
            {amendStep === 1 ? (
              <>
                <h3 className="text-lg font-semibold mb-2">Reason for Amendment</h3>
                <textarea
                  value={amendedReason}
                  onChange={e => setAmendedReason(e.target.value)}
                  className="w-full border rounded p-2"
                  rows={4}
                />
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => setAmendStep(2)}
                    className="px-4 py-2 bg-blue-600 text-white rounded"
                  >
                    Next
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-2">Select Creditors to Serve</h3>
                <MailingMatrixServiceSelector
                  caseId={caseId}
                  value={mailingMatrixService}
                  onChange={service => {
                    setMailingMatrixService(service);
                    setAmendedCreditors(
                      service?.enabled
                        ? (service.recipients || []).map(recipient => ({ ...recipient, method: service.method || 'USM' }))
                        : []
                    );
                  }}
                />
                {!mailingMatrixService?.enabled && <>
                <button
                  onClick={() =>
                    setAmendedCreditors(creditors.map(c => ({ ...c, method: 'USM' })))
                  }
                  className="text-sm text-blue-600 mb-2"
                >
                  Select All as USM
                </button>
                <button
                  onClick={() => setAmendedCreditors([])}
                  className="ml-4 text-sm text-red-600 mb-2"
                >
                  Remove All
                </button>
                <div className="max-h-64 overflow-y-auto mb-4 border">
                  {creditors.map((c, idx) => {
                    const selected = amendedCreditors.find(ac => ac.name === c.name);
                    return (
                      <div key={idx} className="flex items-center justify-between p-2 border-b">
                        <div className="flex-1">
                          <div className="font-medium">{c.name}</div>
                          <div className="text-sm text-gray-500">
                            {c.street1}{c.street2?.trim() ? `, ${c.street2}` : ''}, {c.city}, {c.state} {c.zip_code}
                          </div>
                        </div>
                        <select
                          value={selected?.method || ''}
                          onChange={e => {
                            const method = e.target.value;
                            setAmendedCreditors(prev => {
                              const exists = prev.find(ac => ac.name === c.name);
                              console.log(amendedCreditors)
                              if (exists) {
                                return prev.map(ac =>
                                  ac.name === c.name ? { ...ac, method } : ac
                                );
                              }
                              return [...prev, { ...c, method }];
                            });
                          }}
                          className="border rounded px-2 py-1"
                        >
                          <option value="">None</option>
                          <option value="USM">USM</option>
                          <option value="Certified">Certified</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
                </>}
                <div className="flex justify-between">
                  <button
                    onClick={() => setAmendStep(1)}
                    className="px-4 py-2 bg-gray-200 rounded"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      setShowAmendedModal(false);
                      setData({
                        ...data,
                        amendedReason,
                        amendedCreditors,
                        mailingMatrixService,
                      });
                      console.log(data)
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <IncomeSection
        items={incomeItems}
        caseId={caseId}
        caseData={caseData}
        netIncome={totalIncome}
        onAdd={(item) => setIncomeItems(prev => [...prev, item])}
        onEdit={(updatedCase) => setCaseData(updatedCase)}
        onDelete={(id) => setIncomeItems(prev => prev.filter(i => i.id !== id))}
      />
      <ExpenseSection
        caseId={caseId}
        caseData={caseData}
        netIncome={totalIncome}
        onExpensesChange={(arr) => setExpenses(arr)}
        onDependentsChange={(arr) => setDependents(arr)}
        onInstallmentsChange={(arr) => setInstallments(arr)}
      />
      <div></div>
      {/* ...rest of Schedule IJ schedule panels/content here... */}
      <MonthlyIncomeSection
        caseId={caseId}
        caseData={caseData}
        dependents={dependents}
        onMaritalAdjustmentsChange={setMaritalAdjustments}
        onMeansTestIncomesChange={setMeansTestIncomes}
      />
      <LongFormMeansTestSection
        caseId={caseId}
        caseData={caseData}
        dependents={dependents}
        expenses={expenses}
        creditors={data.allDebts}
        meansTestIncomes={meansTestIncomes}
        maritalAdjustments={maritalAdjustments}
        ready={longFormReady}
        onChange={setLongFormData}
        onMaritalAdjustmentsChange={setMaritalAdjustments}
      />
            {/* Generate Schedule ABC PDF button */}
            
              <div className="flex flex-wrap items-center space-x-4 mb-4">
          {['I','J', '122', 'all'].map(type => {
            const isAll = type === 'all';
            const isSel = selectedSchedules.includes(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setSelectedSchedules(prev => {
                    // clicking “All” resets to just ['all']
                    if (isAll) return ['all'];
                    // otherwise remove 'all'
                    const withoutAll = prev.filter(s => s !== 'all');
                    // toggle this type
                    if (withoutAll.includes(type)) {
                      return withoutAll.filter(s => s !== type);
                    } else {
                      return [...withoutAll, type];
                    }
                  });
                }}
                className={`px-4 py-2 rounded ${
                  isSel ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                {isAll ? 'All' : `Schedule ${type}`}
              </button>
            );
          })}
                        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            checked={data.amended}
            onChange={e => {
              const checked = e.target.checked;
              setData({ ...data, amended: checked });
              if (checked) {
                setShowAmendedModal(true);
                setAmendStep(1);
              }
            }}
            className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
          />
          <label className="ml-2 text-sm text-gray-700">Amended Filing</label>
        </div>
 </div>
      <div className="mt-8 flex justify-end">
        <button
          className="px-6 py-2 bg-blue-700 text-white rounded hover:bg-blue-800"
          onClick={handleGenerate}
        >
          Generate Schedules IJ 122 PDF
        </button>
      </div>
    </div>
    
  );
}
